// Rummy — Web Audio synths. Cards / shuffle / timer / chips / buzzer.
//
// Design: a single shared AudioContext, unlocked on first user gesture
// (`primeAudio`). Every sound is a short synth bed so we avoid shipping
// any audio asset files. Volume is gated by `sfxEnabled` — the admin-
// panel kill switch + the in-game settings menu both flip this.
//
// Mirrors the shape of games/ludo/sounds.ts so the platform SFX feel
// consistent across games.

let ctx: AudioContext | null = null;
let sfxEnabled = true;

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  const Cls = (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Cls) return null;
  ctx = new Cls();
  return ctx;
}

export function primeAudio() {
  const c = ensureCtx(); if (!c) return;
  if (c.state === 'suspended') c.resume().catch(() => {});
}

export function setSfxEnabled(on: boolean) { sfxEnabled = !!on; }

// Short band-limited noise burst — the core "card snap" building block.
function noiseBurst(vol: number, band: number, duration: number) {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const t = c.currentTime;
  const bufSize = Math.floor(c.sampleRate * duration);
  const buf = c.createBuffer(1, bufSize, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource(); src.buffer = buf;
  const filt = c.createBiquadFilter(); filt.type = 'bandpass'; filt.frequency.value = band;
  const g = c.createGain();
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + duration);
  src.connect(filt); filt.connect(g); g.connect(c.destination);
  src.start(t);
}

function tone(freq: number, dur: number, vol = 0.2, type: OscillatorType = 'sine', slide = 0) {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const t = c.currentTime;
  const o = c.createOscillator(); const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  if (slide !== 0) o.frequency.linearRampToValueAtTime(freq + slide, t + dur);
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.02);
  g.gain.linearRampToValueAtTime(0, t + dur);
  o.connect(g); g.connect(c.destination);
  o.start(t); o.stop(t + dur);
}

// ─── Public SFX API ──────────────────────────────────────────────────
export function playShuffle() {
  // Rapid "brrrrrrrt" — 6 quick noise bursts.
  for (let i = 0; i < 6; i++) setTimeout(() => noiseBurst(0.35, 3200, 0.05), i * 40);
}
export function playDeal()     { noiseBurst(0.45, 2600, 0.08); }
export function playDrawClosed() { noiseBurst(0.65, 3500, 0.08); }
export function playDrawOpen()   { noiseBurst(0.55, 2000, 0.11); }
export function playDiscard()    { noiseBurst(0.8,  1700, 0.09); }
export function playSort() {
  for (let i = 0; i < 5; i++) setTimeout(() => noiseBurst(0.3, 3000 + i * 300, 0.045), i * 35);
}
export function playTick() {
  tone(880, 0.04, 0.12, 'square');
}
export function playUrgent() {
  tone(1100, 0.05, 0.18, 'square');
  setTimeout(() => tone(1100, 0.05, 0.18, 'square'), 100);
}

// ─── Urgency-beep loop (parity with games/ludo/sounds.ts) ────────────
//
// Page.tsx triggers this when the turn-timer enters its final
// `URGENCY_BEEP_LEAD_SECONDS` window. Stops automatically on
// stop call OR when the bar visually ends. Exactly the same loop
// shape as Ludo so admins tuning timing perceive identical pacing
// across games.

let _urgencyTimer: number | null = null;
let _urgencyTick = 0;

export function startUrgencyBeep() {
  if (!sfxEnabled) return;
  if (_urgencyTimer != null) return;
  _urgencyTick = 0;
  const tick = () => {
    if (!sfxEnabled) return;
    // Alternating two-tone pattern (880 / 1320 Hz) for a clear
    // "time is running out" cadence. Same as Ludo's pattern.
    const f = (_urgencyTick % 2 === 0) ? 880 : 1320;
    tone(f, 0.05, 0.16, 'square');
    _urgencyTick++;
  };
  tick();
  _urgencyTimer = window.setInterval(tick, 600);
}

export function stopUrgencyBeep() {
  if (_urgencyTimer != null) {
    window.clearInterval(_urgencyTimer);
    _urgencyTimer = null;
  }
  _urgencyTick = 0;
}
export function playWin() {
  const notes = [261.63, 329.63, 392.0, 523.25];
  notes.forEach((f, i) => setTimeout(() => tone(f, 0.32, 0.28, 'sine'), i * 100));
}
export function playChipsClink() {
  for (let i = 0; i < 4; i++) setTimeout(() => noiseBurst(0.35, 5500, 0.06), i * 60);
}
export function playInvalid() {
  tone(120, 0.35, 0.3, 'sawtooth', -40);
}
export function playWhoosh() {
  tone(200, 0.22, 0.22, 'triangle', 500);
}

// ─── Shared platform SFX (copied from games/ludo/sounds.ts) ─────────
// Keeping these in rummy's local module means rummy stays self-contained
// (no cross-game imports) but the timbre / levels match 1:1 with Ludo
// and X&O so the platform feels consistent across games.

// Tactile click for bet tile / settings / player-count toggle.
export function playTap() {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'square';
  const t0 = c.currentTime;
  osc.frequency.setValueAtTime(1200, t0);
  gain.gain.setValueAtTime(0.06, t0);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.05);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.06);
}

// Three rising sine beats — used when a player slot locks / player count
// switches. Pairs with the CSS pop animation on `.wait-slot.locked`.
export function playSlotLock() {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const t0 = c.currentTime;
  const notes = [
    { f: 523.25, dt: 0.00 }, // C5
    { f: 659.25, dt: 0.06 }, // E5
    { f: 880.00, dt: 0.12 }, // A5
  ];
  for (const n of notes) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    const ts = t0 + n.dt;
    osc.frequency.setValueAtTime(n.f, ts);
    gain.gain.setValueAtTime(0.0001, ts);
    gain.gain.exponentialRampToValueAtTime(0.12, ts + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ts + 0.14);
    osc.connect(gain).connect(c.destination);
    osc.start(ts);
    osc.stop(ts + 0.16);
  }
}

// Warm bell chime — when the turn becomes yours. Two sines struck on a
// perfect fifth (C5+G5) + a high overtone for sparkle. Timbrally DISTINCT
// from every other rummy sound so the player recognises it instantly.
export function playYourTurnChime() {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const t0 = c.currentTime;
  const partials = [
    { f: 523.25, gain: 0.11 }, // C5
    { f: 783.99, gain: 0.09 }, // G5
    { f: 1568.0, gain: 0.03 }, // G6
  ];
  for (const p of partials) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(p.f, t0);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(p.gain, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.6);
    osc.connect(gain).connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.65);
  }
}

// Descending "nope" buzzer — fires on invalid declaration / cancel.
export function playCancelSound() {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const t0 = c.currentTime;
  const steps = [
    { f: 380, start: 0.00, dur: 0.13 },
    { f: 220, start: 0.14, dur: 0.18 },
  ];
  for (const s of steps) {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'square';
    const ts = t0 + s.start;
    osc.frequency.setValueAtTime(s.f, ts);
    gain.gain.setValueAtTime(0.18, ts);
    gain.gain.exponentialRampToValueAtTime(0.001, ts + s.dur);
    osc.connect(gain).connect(c.destination);
    osc.start(ts);
    osc.stop(ts + s.dur);
  }
}

// Descending 4-note loss tone (C-Bb-G-Eb). Played on any non-win result.
export function playLossTone() {
  if (!sfxEnabled) return;
  const c = ensureCtx(); if (!c) return;
  const notes = [523.25, 466.16, 392.0, 311.13]; // C5 Bb4 G4 Eb4
  const t0 = c.currentTime;
  notes.forEach((f, i) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'sine';
    const ts = t0 + i * 0.18;
    osc.frequency.setValueAtTime(f, ts);
    gain.gain.setValueAtTime(0.0001, ts);
    gain.gain.exponentialRampToValueAtTime(0.15, ts + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ts + 0.5);
    osc.connect(gain).connect(c.destination);
    osc.start(ts);
    osc.stop(ts + 0.55);
  });
}
